/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  MaritimeWarningItem,
  MaritimeWarningsConfig,
  MaritimeWarningsDomain,
} from './types';
import type {
  MaritimeWarning,
  MaritimeWarningsType,
} from '../../store/warningsForm/types';
import { PublicWarningStatus } from '../../store/warningsForm/types';

const isWarningEmpty = (item: MaritimeWarningItem): boolean => {
  const {
    windDirectionFrom,
    windForce,
    changeType,
    windDirectionTo,
    assignedAreaIds,
  } = item;
  if (assignedAreaIds.length) {
    return false;
  }

  return [windDirectionFrom, windForce, changeType, windDirectionTo].every(
    (value) => value === undefined || value.trim() === '',
  );
};

const isWarningValid = (
  item: MaritimeWarningItem,
  config: MaritimeWarningsConfig,
): boolean => {
  const {
    windDirectionFrom,
    windForce,
    assignedAreaIds,
    changeType,
    windDirectionTo,
  } = item;

  // Direction, windspeed and areas are required.
  if (!windDirectionFrom || !windForce || !assignedAreaIds.length) {
    return false;
  }

  // If there is a Change selected, Direction to is also required
  if (changeType && changeType !== 'NO_CHANGE' && !windDirectionTo) {
    return false;
  }

  if (windDirectionTo && changeType) {
    const result = validateWindDirectionChange(
      windDirectionFrom,
      windDirectionTo,
      changeType,
      config,
    );
    return result !== 'error';
  }
  return true;
};

export const isWarningsValid = (
  warnings: MaritimeWarningItem[],
  config: MaritimeWarningsConfig,
  allowEmpty = true,
): boolean => {
  // One single empty row is allowed, if not empty it should be valid
  if (allowEmpty && warnings.length === 1) {
    return isWarningEmpty(warnings[0]) || isWarningValid(warnings[0], config);
  }
  return warnings.every(
    (warning) => !isWarningEmpty(warning) && isWarningValid(warning, config),
  );
};

const getLevelFromWindForce = (
  maritimeConfig: MaritimeWarningsConfig,
  windForce: string,
): string => {
  const period = maritimeConfig.maritimeLevelDefinition.maritimeLevelsSummer
    ? 'maritimeLevelsSummer'
    : 'maritimeLevelsWinter';

  const levelInfo = maritimeConfig.maritimeLevelDefinition?.[period].find(
    (level) => level?.windForce === windForce,
  );
  return levelInfo?.level || 'extreme';
};

export const getType = (
  domain: MaritimeWarningsDomain,
): MaritimeWarningsType => {
  switch (domain) {
    case 'coastalWaters':
      return 'maritimeWarningCoastal';
    case 'northSeaDistricts':
    default:
      return 'maritimeWarningNorthSea';
  }
};

export const mapItemsToMaritimeWarnings = (
  warnings: MaritimeWarningItem[],
  domain: MaritimeWarningsDomain,
  maritimeConfig: MaritimeWarningsConfig,
  editor?: string,
  shouldExtend = false,
): MaritimeWarning[] => {
  const type = getType(domain);
  const now = Date.now();
  if (warnings.length === 1 && warnings[0].assignedAreaIds.length === 0) {
    // 1 warning without any areas means cancel/withdraw all warnings
    return [
      {
        type,
        editor,
        status: PublicWarningStatus.PUBLISHED,
        warningDetail: {
          areas: [],
          domain: type,
        },
      },
    ] as unknown as MaritimeWarning[];
  }
  return warnings.map((warning) => ({
    type,
    editor,
    status: PublicWarningStatus.PUBLISHED,
    warningDetail: {
      phenomenon: 'wind',
      validFrom:
        shouldExtend && warning.validFrom
          ? warning.validFrom // keep original validFrom when extending
          : new Date(now).toISOString(),
      validUntil: new Date(now + 24 * 60 * 60 * 1000).toISOString(),
      level: getLevelFromWindForce(maritimeConfig, warning.windForce || ''),
      probability: 100,
      areas: warning.assignedAreaIds.map((areaId) => {
        const areaDetails = maritimeConfig.districtList[domain].find(
          (district) => district.districtName === areaId,
        );
        return {
          objectName: areaId,
          geoJSON: areaDetails?.geoJSON || {
            type: 'FeatureCollection',
            features: [],
          },
        };
      }),
      windDirectionFrom: warning.windDirectionFrom,
      windForce: warning.windForce,
      changeType: warning.changeType || 'NO_CHANGE',
      windDirectionTo: warning.windDirectionTo,
      descriptionOriginal: '',
      descriptionTranslation: '',
      domain: type,
    },
  }));
};

export const mapMaritimeWarningsToItems = (
  warnings: MaritimeWarning[],
): MaritimeWarningItem[] => {
  return warnings.map((warning) => ({
    id: warning.id || '',
    assignedAreaIds:
      warning.warningDetail.areas?.map((area) => area.objectName) || [],
    windDirectionFrom: warning.warningDetail.windDirectionFrom,
    windDirectionTo: warning.warningDetail.windDirectionTo,
    windForce: warning.warningDetail.windForce,
    changeType: warning.warningDetail.changeType,
    validFrom: warning.warningDetail.validFrom,
    validUntil: warning.warningDetail.validUntil,
  }));
};

const getDegreesFromDirection = (
  maritimeConfig: MaritimeWarningsConfig,
  direction: string,
): number | undefined => {
  const directionDefinition = maritimeConfig.windDirectionDefinition.find(
    (item) => item.value === direction,
  );
  return directionDefinition?.degrees ?? undefined;
};

export const validateWindDirectionChange = (
  directionFrom: string | undefined,
  directionTo: string | undefined,
  changeType: string | undefined,
  config?: MaritimeWarningsConfig,
): true | 'warning' | 'error' => {
  if (
    !directionFrom ||
    !directionTo ||
    !changeType ||
    !config ||
    changeType === 'NO_CHANGE'
  ) {
    return true;
  }

  const directionFromDegrees = getDegreesFromDirection(config, directionFrom);
  const directionToDegrees = getDegreesFromDirection(config, directionTo);

  if (directionFromDegrees === undefined || directionToDegrees === undefined) {
    return true;
  }

  if (directionFromDegrees === directionToDegrees) {
    return 'error';
  }
  const max = config.validation.dirTo.max_wind_change;
  const min = config.validation.dirTo.min_wind_change;

  // Calculate the absolute angular difference (0-360)
  const diff =
    changeType === 'BACKING'
      ? (directionFromDegrees - directionToDegrees + 360) % 360
      : (directionToDegrees - directionFromDegrees + 360) % 360;

  if (diff > max.value) {
    return max.level;
  }
  if (diff < min.value) {
    return min.level;
  }
  return true;
};
